(() => {
  // A single paint operation owns each complete arrow, including the loop reset.
  // This avoids independent SVG/CSS animation clocks flashing on mobile WebKit.
  const initAutumnArrows = (root, autumn) => {
    if (!autumn || !window.Path2D) return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const art = autumn.querySelector('.pav-autumn-art');
    const arrows = [];
    autumn.querySelectorAll('.pav-autumn-callout svg').forEach((svg, index) => {
      const shaft = svg.querySelector('.pav-autumn-arrow-line');
      const tip = svg.querySelector('.pav-autumn-arrow-tip');
      if (!shaft || !tip) return;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;
      canvas.className = 'pav-autumn-arrow-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      svg.after(canvas);
      arrows.push({ svg, canvas, context, index, shaft: new Path2D(shaft.getAttribute('d')), tip: new Path2D(tip.getAttribute('d')), length: shaft.getTotalLength(), view: svg.viewBox.baseVal });
    });
    if (!arrows.length) return;
    let elapsed = 0, previous = null, frame = 0, visible = true, disposed = false;
    const still = () => preference.matches || autumn.classList.contains('pav-motion-paused');
    const paint = (arrow, phase, staticFrame) => {
      const { context: ctx, canvas, view, width, height, ratio } = arrow;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!staticFrame && (phase < 0 || phase >= 2450)) return;
      const position = Math.min(1, phase / 1300);
      const progress = staticFrame ? 1 : (1 - Math.cos(Math.PI * position)) / 2;
      const alpha = staticFrame || phase <= 2050 ? 1 : (1 + Math.cos(Math.PI * (phase - 2050) / 400)) / 2;
      const scale = Math.min(width / view.width, height / view.height);
      ctx.setTransform(ratio * scale, 0, 0, ratio * scale, ratio * (width - view.width * scale) / 2, ratio * (height - view.height * scale) / 2);
      ctx.strokeStyle = arrow.color;
      ctx.lineWidth = arrow.stroke;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = alpha;
      ctx.setLineDash([arrow.length, arrow.length]);
      ctx.lineDashOffset = arrow.length * (1 - progress);
      if (progress > 0) ctx.stroke(arrow.shaft);
      if (staticFrame || phase > 1300) {
        ctx.setLineDash([]);
        ctx.lineDashOffset = 0;
        ctx.globalAlpha = alpha * (staticFrame ? 1 : Math.min(1, (phase - 1300) / 160));
        ctx.stroke(arrow.tip);
      }
      ctx.globalAlpha = 1;
    };
    const draw = () => arrows.forEach(arrow => {
      const local = elapsed - arrow.index * 220;
      paint(arrow, local < 0 ? -1 : local % 2600, still());
    });
    const resize = () => {
      arrows.forEach(arrow => {
        const style = getComputedStyle(arrow.svg);
        const width = parseFloat(style.width), height = parseFloat(style.height);
        const ratio = Math.min(window.devicePixelRatio || 1, 3);
        Object.assign(arrow, { width, height, ratio, color: style.stroke, stroke: parseFloat(style.strokeWidth) });
        ['left', 'right', 'top', 'bottom'].forEach(key => { arrow.canvas.style[key] = style[key]; });
        arrow.canvas.style.width = width + 'px';
        arrow.canvas.style.height = height + 'px';
        arrow.canvas.width = Math.max(1, Math.round(width * ratio));
        arrow.canvas.height = Math.max(1, Math.round(height * ratio));
      });
      draw();
    };
    const tick = now => {
      frame = 0;
      if (disposed || !visible || document.hidden || still()) { previous = null; return; }
      if (previous !== null) elapsed += Math.min(now - previous, 64);
      previous = now;
      draw();
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0; previous = null;
      draw();
      if (!disposed && visible && !document.hidden && !still()) frame = requestAnimationFrame(tick);
    };
    resize();
    autumn.classList.add('pav-canvas-arrows');
    let visibilityObserver, sizeObserver;
    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { rootMargin: '40px' });
      visibilityObserver.observe(art);
    }
    if ('ResizeObserver' in window) {
      sizeObserver = new ResizeObserver(resize);
      sizeObserver.observe(art);
      arrows.forEach(arrow => sizeObserver.observe(arrow.svg.parentElement));
    }
    window.addEventListener('resize', resize, { passive: true });
    document.addEventListener('visibilitychange', sync);
    preference.addEventListener('change', sync);
    (root.closest('.shopify-section') || root).addEventListener('shopify:section:unload', () => {
      disposed = true;
      cancelAnimationFrame(frame);
      visibilityObserver?.disconnect(); sizeObserver?.disconnect();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', sync);
      preference.removeEventListener('change', sync);
    }, { once: true });
    sync();
  };


  // The still photograph is the first paint; motion is an optional visible-only layer.
  const initHeroFilm = (root, autumn) => {
    const film = autumn?.querySelector('[data-pav-hero-film]');
    if (!film) return;
    const photo = autumn.querySelector('.pav-autumn-scene-photo');
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false, loaded = false, disposed = false, finished = false;
    film.loop = false;
    const allowed = () => !disposed && visible && !document.hidden && !preference.matches && !autumn.classList.contains('pav-motion-paused');
    const sync = () => {
      // Keep the final decoded frame; returning to the hero must never replay it.
      if (finished || film.ended) {
        finished = true;
        film.pause();
        film.classList.toggle('pav-film-playing', !preference.matches);
        return;
      }
      if (!allowed() || (photo && !photo.complete)) { film.pause(); return; }
      if (!loaded) {
        loaded = true;
        film.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
        film.load();
      }
      film.muted = true;
      film.playsInline = true;
      film.play().catch(() => { if (!finished) film.classList.remove('pav-film-playing'); });
    };
    const playing = () => { if (allowed()) film.classList.add('pav-film-playing'); else film.pause(); };
    const ended = () => { finished = true; film.pause(); film.classList.add('pav-film-playing'); };
    const failed = () => film.classList.remove('pav-film-playing');
    const preferenceChanged = () => { if (preference.matches) failed(); sync(); };
    film.addEventListener('playing', playing);
    film.addEventListener('ended', ended);
    film.addEventListener('error', failed);
    photo?.addEventListener('load', sync, {once:true});
    photo?.addEventListener('error', sync, {once:true});
    document.addEventListener('visibilitychange', sync);
    preference.addEventListener('change', preferenceChanged);
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, {threshold:0.08});
      observer.observe(film.parentElement);
    } else { visible = true; sync(); }
    (root.closest('.shopify-section') || root).addEventListener('shopify:section:unload', () => {
      disposed = true; film.pause(); observer?.disconnect();
      photo?.removeEventListener('load', sync); photo?.removeEventListener('error', sync);
      film.removeEventListener('playing', playing); film.removeEventListener('ended', ended); film.removeEventListener('error', failed);
      document.removeEventListener('visibilitychange', sync);
      preference.removeEventListener('change', preferenceChanged);
    }, {once:true});
  };


  const init = (root) => {
    if (!root || root.dataset.bound) return;
    root.dataset.bound = 'true';
    if ('IntersectionObserver' in window) {
      const motionObserver = new IntersectionObserver(entries => entries.forEach(entry => root.classList.toggle('pav-outside', !entry.isIntersecting)), { rootMargin: '80px' });
      motionObserver.observe(root);
      root.addEventListener('shopify:section:unload', () => motionObserver.disconnect(), { once: true });
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduced ? 'auto' : 'smooth';
    const autumn = root.querySelector('[data-pav-autumn]');
    initAutumnArrows(root, autumn);
    initHeroFilm(root, autumn);
    if (root.classList.contains('pav-collections-section') && !reduced && 'IntersectionObserver' in window) {
      const entrance = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { root.classList.add('pav-collections-entered'); entrance.disconnect(); }
      }, {threshold:0.12});
      entrance.observe(root);
      (root.closest('.shopify-section') || root).addEventListener('shopify:section:unload', () => entrance.disconnect(), {once:true});
    }
    const motionToggle = root.querySelector('[data-pav-motion-toggle]');
    if (autumn && motionToggle) {
      motionToggle.hidden = false;
      const setMotion = paused => {
        autumn.classList.toggle('pav-motion-paused', paused);
        motionToggle.setAttribute('aria-pressed', String(paused));
        motionToggle.setAttribute('aria-label', paused ? motionToggle.dataset.playLabel : motionToggle.dataset.pauseLabel);
      };
      setMotion(reduced);
      motionToggle.addEventListener('click', () => setMotion(!autumn.classList.contains('pav-motion-paused')));
      const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      const handleMotionPreference = event => setMotion(event.matches);
      motionPreference.addEventListener('change', handleMotionPreference);
      root.addEventListener('shopify:section:unload', () => motionPreference.removeEventListener('change', handleMotionPreference), {once:true});
    }
    const slides = [...root.querySelectorAll('[data-pav-slide]')], track = root.querySelector('[data-pav-slides]'), dots = [...root.querySelectorAll('[data-pav-dot]')];
    let current = 0;
    const fitSlide = () => { if(track && slides[current]) track.style.height = slides[current].offsetHeight + 'px'; };
    const show = i => { current = (i + slides.length) % slides.length; track.style.transform = 'translateX(-' + current * 100 + '%)'; slides.forEach((slide,n) => { slide.inert = n !== current; slide.setAttribute('aria-hidden', n !== current); }); dots.forEach((dot,n) => dot.setAttribute('aria-pressed', n === current)); const counter=root.querySelector('[data-pav-current]'); if(counter) counter.textContent=String(current+1).padStart(2,'0'); fitSlide(); };
    root.querySelector('[data-pav-prev]')?.addEventListener('click', () => show(current-1));
    root.querySelector('[data-pav-next]')?.addEventListener('click', () => show(current+1));
    dots.forEach((dot,n) => dot.addEventListener('click', () => show(n)));
    if (slides.length) {
      show(0);
      if ('ResizeObserver' in window) {
        const slideObserver = new ResizeObserver(fitSlide);
        slides.forEach(slide => slideObserver.observe(slide));
        root.addEventListener('shopify:section:unload', () => slideObserver.disconnect(), {once:true});
      } else {
        window.addEventListener('resize', fitSlide);
        root.addEventListener('shopify:section:unload', () => window.removeEventListener('resize', fitSlide), {once:true});
      }
    }
    root.addEventListener('shopify:block:select',e=>{const n=slides.findIndex(s=>s.contains(e.target)||s===e.target);if(n>=0)show(n);});
    let touchX = 0, touchY = 0;
    track?.addEventListener('touchstart',e=>{touchX=e.changedTouches[0].clientX;touchY=e.changedTouches[0].clientY;},{passive:true});
    track?.addEventListener('touchend',e=>{const dx=e.changedTouches[0].clientX-touchX,dy=e.changedTouches[0].clientY-touchY;if(Math.abs(dx)>55&&Math.abs(dx)>Math.abs(dy)*1.5)show(current+(dx<0?1:-1));},{passive:true});
    const tabs = [...root.querySelectorAll('[data-pav-tab]')];
    const selectTab = tab => { tabs.forEach(t=>{const active=t===tab;t.setAttribute('aria-selected',active);t.tabIndex=active?0:-1;});root.querySelectorAll('[data-pav-panel]').forEach(p=>{p.hidden=p.dataset.pavPanel!==tab.dataset.pavTab;});window.dispatchEvent(new Event('resize')); };
    tabs.forEach((tab,i)=>{tab.addEventListener('click',()=>selectTab(tab));tab.addEventListener('keydown',e=>{if(['ArrowRight','ArrowLeft','Home','End'].includes(e.key)){e.preventDefault();let n=e.key==='Home'?0:e.key==='End'?tabs.length-1:(i+(e.key==='ArrowRight'?1:-1)+tabs.length)%tabs.length;selectTab(tabs[n]);tabs[n].focus();}});});
    root.querySelectorAll('[data-pav-panel]').forEach(panel=>{
      const rail=panel.querySelector('[data-pav-products]'),prev=panel.querySelector('[data-pav-products-prev]'),next=panel.querySelector('[data-pav-products-next]'),container=panel.querySelector('[data-pav-product-dots]');
      const update=()=>{if(panel.hidden)return;const max=rail.scrollWidth-rail.clientWidth;prev.disabled=rail.scrollLeft<2;next.disabled=rail.scrollLeft>=max-2;const pages=Math.max(1,Math.ceil(rail.scrollWidth/rail.clientWidth));if(container&&container.children.length!==pages){container.replaceChildren();for(let i=0;i<pages;i++){const b=document.createElement('button');b.type='button';b.setAttribute('aria-label',String(i+1));b.addEventListener('click',()=>rail.scrollTo({left:i*rail.clientWidth,behavior}));container.append(b);}}if(container)[...container.children].forEach((b,i)=>b.setAttribute('aria-current',i===Math.round(rail.scrollLeft/rail.clientWidth)));};
      prev.addEventListener('click',()=>rail.scrollBy({left:-rail.clientWidth,behavior}));next.addEventListener('click',()=>rail.scrollBy({left:rail.clientWidth,behavior}));rail.addEventListener('scroll',update,{passive:true});window.addEventListener('resize',update);update();
    });
    const rail=root.querySelector('[data-pav-rail]'),previous=root.querySelector('[data-pav-rail-prev]'),next=root.querySelector('[data-pav-rail-next]');
    if(rail){const update=()=>{previous.disabled=rail.scrollLeft<2;next.disabled=rail.scrollLeft>=rail.scrollWidth-rail.clientWidth-2;};previous.addEventListener('click',()=>rail.scrollBy({left:-rail.clientWidth/2,behavior}));next.addEventListener('click',()=>rail.scrollBy({left:rail.clientWidth/2,behavior}));rail.addEventListener('scroll',update,{passive:true});window.addEventListener('resize',update);update();}
    root.querySelectorAll('[data-pav-player]').forEach(player => {
      const video=player.querySelector('video'), play=player.querySelector('[data-pav-play]'), toolbar=player.querySelector('[data-pav-toolbar]'), toggle=player.querySelector('[data-pav-toggle]'), label=player.querySelector('[data-pav-toggle-label]'), seek=player.querySelector('[data-pav-seek]'), mute=player.querySelector('[data-pav-mute]'), error=player.querySelector('[data-pav-video-error]');
      if(!video)return;
      video.controls=false;play.hidden=false;
      const sync=()=>{play.hidden=!video.paused;label.textContent=video.paused?player.dataset.playLabel:player.dataset.pauseLabel;toggle.setAttribute('aria-label',label.textContent);};
      const start=()=>{error.hidden=true;root.querySelectorAll('video').forEach(other=>{if(other!==video)other.pause();});video.play().then(()=>{toolbar.hidden=false;sync();}).catch(()=>{error.hidden=false;sync();});};
      play.addEventListener('click',start);
      toggle.addEventListener('click',()=>video.paused?start():video.pause());
      video.addEventListener('click',()=>video.paused?start():video.pause());
      video.addEventListener('play',sync);video.addEventListener('pause',sync);video.addEventListener('ended',()=>{toolbar.hidden=true;sync();});
      video.addEventListener('timeupdate',()=>{if(Number.isFinite(video.duration)&&video.duration>0)seek.value=100*video.currentTime/video.duration;});
      seek.addEventListener('input',()=>{if(Number.isFinite(video.duration))video.currentTime=video.duration*Number(seek.value)/100;});
      mute.addEventListener('click',()=>{video.muted=!video.muted;mute.textContent=video.muted?player.dataset.unmuteLabel:player.dataset.muteLabel;mute.setAttribute('aria-label',mute.textContent);});
      root.addEventListener('shopify:section:unload',()=>video.pause(),{once:true});
      sync();
    });
    const input=root.querySelector('[data-pav-search]'),results=root.querySelector('[data-pav-results]');let timer,controller;
    input?.addEventListener('input',()=>{clearTimeout(timer);controller?.abort();const q=input.value.trim();if(q.length<2){results.hidden=true;return;}timer=setTimeout(async()=>{controller=new AbortController();try{const prefix=window.Shopify?.routes?.root||'/';const response=await fetch(prefix+'search/suggest.json?q='+encodeURIComponent(q)+'&resources[type]=product&resources[limit]=5',{signal:controller.signal});if(!response.ok)return;const data=await response.json();results.replaceChildren();(data.resources?.results?.products||[]).forEach(p=>{const a=document.createElement('a');a.href=p.url;const img=document.createElement('img');img.src=p.image;img.alt='';const name=document.createElement('span');name.textContent=p.title;a.append(img,name);results.append(a);});results.hidden=!results.childElementCount;}catch(error){if(error.name!=='AbortError')results.hidden=true;}},250);});
    input?.addEventListener('keydown',e=>{if(e.key==='Escape')results.hidden=true;if(e.key==='ArrowDown'&&!results.hidden){e.preventDefault();results.querySelector('a')?.focus();}});
    document.addEventListener('click',e=>{if(results&&!e.target.closest('.pav-search-wrap'))results.hidden=true;});
  };
  const run=()=>document.querySelectorAll('[data-pav-v3]').forEach(init);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',run);else run();
  document.addEventListener('shopify:section:load',run);
})();
